/**
 * Bundle Add to Cart - Custom handler for adding 2 products simultaneously
 * Clones Vision theme's ProductForm functionality
 * Independent of theme's core JS - won't break existing functionality
 */

if (!customElements.get('bundle-product-form')) {
  class BundleProductForm extends HTMLElement {
    constructor() {
      super();
      
      this.form = this.querySelector('form');
      this.submitButtons = this.querySelectorAll('[type="submit"]');
      this.body = document.body;
      
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      
      // Show loading state on button
      this.submitButtons.forEach((submitButton) => {
        submitButton.classList.add('loading');
        submitButton.setAttribute('aria-disabled', true);
      });

      // Prepare fetch config
      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };

      // Get form data
      let formData = new FormData(this.form);
      
      // Generate unique Set ID (timestamp + random for uniqueness)
      const setId = `set_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Handle line item properties for both products
      const product1Properties = {};
      const product2Properties = {};
      const keysToDelete = [];
      
      // Extract line item properties
      for (let [key, value] of formData.entries()) {
        // Product 1 properties: properties_product_1[PropertyName]
        if (key.startsWith('properties_product_1[')) {
          const match = key.match(/properties_product_1\[([^\]]+)\]/);
          if (match && match[1]) {
            product1Properties[match[1]] = value;
            keysToDelete.push(key);
          }
        }
        // Product 2 properties: properties_product_2[PropertyName]
        else if (key.startsWith('properties_product_2[')) {
          const match = key.match(/properties_product_2\[([^\]]+)\]/);
          if (match && match[1]) {
            product2Properties[match[1]] = value;
            keysToDelete.push(key);
          }
        }
        // Legacy Product 2 properties: properties[PropertyName]
        else if (key.startsWith('properties[') && !key.includes('items[')) {
          const match = key.match(/properties\[([^\]]+)\]/);
          if (match && match[1]) {
            product2Properties[match[1]] = value;
            keysToDelete.push(key);
          }
        }
      }
      
      // Remove original property keys
      keysToDelete.forEach(key => formData.delete(key));
      
      // Count items being added
      let itemCount = 0;
      for (let [key] of formData.entries()) {
        if (key.match(/^items\[\d+\]\[id\]$/)) {
          itemCount++;
        }
      }
      
      // Add properties to Product 1 (items[0])
      if (itemCount >= 1) {
        // Add Set ID (hidden property with underscore prefix - Shopify won't display it)
        formData.append(`items[0][properties][_Set ID]`, setId);
        
        // Add user-visible properties
        Object.keys(product1Properties).forEach(propName => {
          formData.append(`items[0][properties][${propName}]`, product1Properties[propName]);
        });
      }
      
      // Add properties to Product 2 (items[1])
      if (itemCount >= 2) {
        // Add Set ID (hidden property with underscore prefix - Shopify won't display it)
        formData.append(`items[1][properties][_Set ID]`, setId);
        
        // Add user-visible properties
        Object.keys(product2Properties).forEach(propName => {
          formData.append(`items[1][properties][${propName}]`, product2Properties[propName]);
        });
      }
      
      // Append sections to update (same as theme)
      formData.append('sections', this.getSectionsToRender().map((section) => section.section).join(','));
      formData.append('sections_url', window.location.pathname);
      
      config.body = formData;

      // Submit to Shopify cart
      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          // Handle errors
          if (response.status) {
            this.handleErrorMessage(response.description);
            
            // Dispatch error event (Vision theme pattern)
            this.dispatchCustomEvent('product:variant-error', {
              source: 'bundle-product-form',
              errors: response.description,
              message: response.message
            });
            
            // Refresh cart if needed
            if (response.status === 422) {
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true
              }));
            }
            return;
          }

          // Success! Update cart UI
          this.renderContents(response);

          // Dispatch cart:item-added event (Vision theme event)
          this.dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          console.error('Bundle Add to Cart Error:', e);
          this.handleErrorMessage('An error occurred. Please try again.');
        })
        .finally(() => {
          // Remove loading state
          this.submitButtons.forEach((submitButton) => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          });
        });
    }

    /**
     * Get sections to update after add to cart
     * Same as Vision theme's ProductForm
     */
    getSectionsToRender() {
      return [
        {
          id: 'Cart',
          section: 'main-cart',
          selector: '.thb-cart-form'
        },
        {
          id: 'Cart-Drawer',
          section: 'cart-drawer',
          selector: '.cart-drawer'
        },
        {
          id: 'cart-drawer-toggle',
          section: 'cart-bubble',
          selector: '.thb-item-count'
        }
      ];
    }

    /**
     * Update cart drawer and bubble after successful add to cart
     * Cloned from Vision theme's renderContents method
     */
    renderContents(parsedState) {
      // Update each section
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) {
          return;
        }
        
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        // Reinitialize Cart Drawer events if needed
        if (section.id === 'Cart-Drawer') {
          document.getElementById('Cart-Drawer')?.removeProductEvent();
        }
        
        // Reinitialize Cart if needed
        if (typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      }));

      // Open cart drawer (Vision theme pattern)
      let product_drawer = document.getElementById('Product-Drawer'),
        search_drawer = document.getElementById('Search-Drawer');

      if (search_drawer && search_drawer.classList.contains('active')) {
        search_drawer.classList.remove('active');
      }
      
      if (product_drawer && product_drawer.contains(this)) {
        product_drawer.classList.remove('active');
        this.body.classList.remove('open-cc--product');
        if (document.getElementById('Cart-Drawer')) {
          document.getElementById('Cart-Drawer').open();
        }
      } else if (document.getElementById('Cart-Drawer')) {
        document.getElementById('Cart-Drawer').open();
      }
    }

    /**
     * Parse section HTML (Vision theme helper)
     */
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }

    /**
     * Show error message
     */
    handleErrorMessage(errorMessage = false) {
      const errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
      const errorMessageElement = errorMessageWrapper?.querySelector('.product-form__error-message');

      if (!errorMessageWrapper || !errorMessageElement) {
        return;
      }

      if (errorMessage) {
        errorMessageElement.textContent = errorMessage;
        errorMessageWrapper.removeAttribute('hidden');
      } else {
        errorMessageWrapper.setAttribute('hidden', '');
        errorMessageElement.textContent = '';
      }
    }

    /**
     * Dispatch custom events (Vision theme pattern)
     */
    dispatchCustomEvent(eventName, detail = {}) {
      const event = new CustomEvent(eventName, {
        detail: detail,
        bubbles: true,
        cancelable: true
      });
      document.dispatchEvent(event);
    }
  }

  // Register custom element
  customElements.define('bundle-product-form', BundleProductForm);
}

